import { canv } from "@/canv/global.js";
import {
    getNumberProperty,
    getStringProperty,
    isBrowser,
    normalizeImageUrl,
} from "@/canv/utils.js";
import { getViewport } from "@/canvasController/helpers";
import {
    IMAGE_LOAD_BORDER_COLOR,
    IMAGE_LOAD_LIMIT,
    IMAGE_PREVIEW_PARAM_KEY,
} from "@/constants";
import { elementBase } from "@/elementBase/elementBase.js";
import { limiter } from "@/helpers/limiter";
import { widgetOption } from "@/widgetMenu/widgetMenu";

export const imageLoadLimiter = limiter(IMAGE_LOAD_LIMIT);

const LOD_LOW = 0.1;
const LOD_LOW_HIGH = 0.3; // для максимального качества при экспорте в выс качестве 8к картинок
const LOD_MEDIUM = 0.5;
const LOD_HIGH = 1;

const LODS = [LOD_LOW, LOD_LOW_HIGH, LOD_MEDIUM, LOD_HIGH].sort();
const LOD_PRIORITIES = LODS.sort((a, b) => b - a);

let DRAW_ERROR_LOGGED = false;

const getLodByScale = (scale) => {
    if (scale < 0.2) {
        return LOD_LOW;
    }
    if (scale < 1) {
        return LOD_MEDIUM;
    }
    return LOD_HIGH;
};

export const getLodByResolution = (widget) => {
    if (widget.width > 4000 || widget.height > 4000) return LOD_LOW_HIGH;

    if (widget.width > 2000 || widget.height > 2000) return LOD_MEDIUM;

    return LOD_HIGH;
};

const createImageCanvas = (image, { resizeWidth, resizeHeight }) => {
    return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(resizeWidth);
        canvas.height = Math.floor(resizeHeight);
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

        setTimeout(() => {
            resolve(canvas);
        }, 10);
    });
};

export const createImage = (src, decode = false) =>
    new Promise((resolve) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = async () => {
            if (decode) {
                try {
                    await image.decode();
                } catch (e) { }
            }
            resolve(image);
        };
        image.onerror = () => {
            // если не смогли загрузить картинку - возвращаем пустую png 1x1
            const emptyImage = new Image();
            emptyImage.src =
                'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
            emptyImage.onload = () => resolve(emptyImage);
        };
        image.src = src;
    });

export class imageWidget extends elementBase {
    constructor(src, width, height) {
        super();
        this.loading = false;
        if (src != undefined) {
            this.initImage(src);
        }
        this.width = width;
        this.height = height;

        this.clipWidthShare = 1;
        this.clipHeightShare = 1;
        this.clipDxShare = 0;
        this.clipDyShare = 0;

        this.updateDims();
    }

    updateDims() {
        if (!isBrowser()) return;
        this.updateBase(
            this.startX + this.clipDxShare * this.width,
            this.startY + this.clipDyShare * this.height,
            this.width * this.clipWidthShare,
            this.height * this.clipHeightShare,
        );
    }

    draw() {
        // метод draw вызывается только если элемент находится во вьюпорте (shouldDraw)
        // картинка инициализируется только при первом рендере
        // пока идет загрузка изображения - показываем рамку
        const viewport = getViewport();
        const scale = document.body.clientWidth / viewport.viewportWidth;
        const imageScale = this.originalWidth
            ? Math.min((scale * this.baseWidth) / this.originalWidth, 5)
            : Math.min((scale * this.width) / this.width, 5);
        const lod = !canv.isHighPdfExporting
            ? getLodByScale(imageScale)
            : getLodByResolution(this);
        const image = this.getOrInitImage(lod);

        const prevStrokeStyle = canv.ctx.strokeStyle;
        const prevLineWidth = canv.ctx.lineWidth;
        const prevGlobalAlpha = canv.ctx.globalAlpha;
        const prevFont = canv.ctx.font;
        const prevTextBaseline = canv.ctx.textBaseline;
        const prevFillStyle = canv.ctx.fillStyle;
        // canv.ctx.save();
        try {
            if (!image) {
                canv.ctx.strokeStyle = IMAGE_LOAD_BORDER_COLOR;
                const BORDER_WIDTH = 1 / canv.m[0];
                canv.ctx.lineWidth = BORDER_WIDTH;
                canv.ctx.strokeRect(
                    this.startX + BORDER_WIDTH / 2,
                    this.startY + BORDER_WIDTH / 2,
                    this.width - BORDER_WIDTH,
                    this.height - BORDER_WIDTH,
                );
            }
            if (this.loading) {
                canv.ctx.globalAlpha = 0.5;
            }
            if (image) {
                if (!this.isClipping) {
                    canv.ctx.drawImage(
                        image,
                        this.clipDxShare * image.width,
                        this.clipDyShare * image.height, // sx, sy
                        image.width * this.clipWidthShare,
                        image.height * this.clipHeightShare, // sWidth, sHeight
                        this.startX + this.clipDxShare * this.width,
                        this.startY + this.clipDyShare * this.height, // dx, dy
                        this.width * this.clipWidthShare,
                        this.height * this.clipHeightShare, // dWidth, dHeight
                    );
                } else {
                    canv.ctx.drawImage(
                        image,
                        this.startX,
                        this.startY,
                        this.width,
                        this.height,
                    );
                }
            }
            if (Boolean(window["debug"])) {
                const p20 = canv.actionManager.getZoomConstant(20);
                const fontSize = Math.min(this.height / 2, p20);
                canv.ctx.font = `${fontSize}px sans-serif`;
                canv.ctx.textBaseline = "hanging";
                canv.ctx.fillStyle = this.imageLoading ? "red" : "blue";
                canv.ctx.fillText(
                    `lod: ${lod}`,
                    Math.max(this.startX, viewport.viewportStartX) + p20,
                    Math.max(this.startY, viewport.viewportStartY + p20 * 3) + fontSize,
                );
                canv.ctx.fillText(
                    `${(imageScale * 100).toFixed(2)} %`,
                    Math.max(this.startX, viewport.viewportStartX) + p20,
                    Math.max(this.startY, viewport.viewportStartY + p20 * 3) +
                    fontSize * 2,
                );
            }
        } catch (e) {
            console.log(e);
            if (!DRAW_ERROR_LOGGED) {
                DRAW_ERROR_LOGGED = true;
                canv.captureException(e);
            }
        }
        canv.ctx.strokeStyle = prevStrokeStyle;
        canv.ctx.lineWidth = prevLineWidth;
        canv.ctx.globalAlpha = prevGlobalAlpha;
        canv.ctx.font = prevFont;
        canv.ctx.textBaseline = prevTextBaseline;
        canv.ctx.fillStyle = prevFillStyle;
        // canv.ctx.restore();
    }

    translate(x, y) {
        this.startX += x;
        this.startY += y;
        this.updateDims();
    }

    resize(x, y, catchPointer) {
        var diffValue = (x + y) / 2;

        // Насколько надо сильнее менять высоту по сравнению с шириной, чтобы сохранить пропорции
        var hvRatio = this.height / this.width;

        this.height += hvRatio * diffValue;
        this.width += diffValue;
    }

    // обработчики menu

    changeImage(data) {
        this.initImage(data.src);
        this.width = data.obj.width;
        this.height = data.obj.height;

        this.originalWidth = this.width;
        this.originalHeight = this.height;
        this.clipDxShare = 0;
        this.clipDyShare = 0;
        this.clipWidthShare = 1;
        this.clipHeightShare = 1;

        this.updateDims();
    }

    clipImage() {
        var clipTool = canv.toolBox.TryGetToolByName("clipTool");

        var originalArea = {
            x: this.startX,
            y: this.startY,
            width: this.width,
            height: this.height,
        };

        var clipArea = {
            x: this.startX + this.clipDxShare * this.width,
            y: this.startY + this.clipDyShare * this.height,
            width: this.width * this.clipWidthShare,
            height: this.height * this.clipHeightShare,
        };

        var data = {
            originalArea: originalArea,
            clipArea: clipArea,
            UID: this.UID,
        };

        clipTool.setupTool(data);

        this.isClipping = true;

        canv.toolBox.switchTo(clipTool);
    }

    //вызывается из clipTool
    setClip(clipDxShare, clipDyShare, clipWidthShare, clipHeightShare) {
        // первый четыре параматра в drawImage рассчитываются как k * originalWidth...., последние четыре как k * width
        canv.eventManager.openRecord();
        this.fixState("clip");
        this.clipDxShare = clipDxShare;
        this.clipDyShare = clipDyShare;
        this.clipWidthShare = clipWidthShare;
        this.clipHeightShare = clipHeightShare;
        this.saveState("clip");
        canv.eventManager.closeRecord();
        this.isClipping = false;
        this.updateDims();
        canv.redraw();
    }

    getImageByLod = (lod) => {
        const startIndex = LOD_PRIORITIES.indexOf(lod);
        if (startIndex === -1) {
            return undefined;
        }
        // вначале ищем по приоритету, начиная с выбранного lod (high -> low)
        for (let i = startIndex; i < LOD_PRIORITIES.length; i++) {
            const bitmap = this.bitmapLODs.get(LOD_PRIORITIES[i]);
            if (bitmap) {
                return bitmap;
            }
        }
        // если ничего не нашли, но в теории может быть доступно более качественное изображение - показываем его
        // low -> high
        return (
            this.bitmapLODs.get(LOD_LOW) ||
            this.bitmapLODs.get(LOD_MEDIUM) ||
            this.bitmapLODs.get(LOD_HIGH)
        );
    };

    generateBitmap = async (image, lod) => {
        const startTime = window.performance.now();
        const bitmap = await createImageCanvas(image, {
            resizeWidth: image.width * lod,
            resizeHeight: image.height * lod,
            resizeQuality: "high",
        });
        if (window.debug) {
            const endTime = window.performance.now();
            console.log(`Time Elapsed (${lod}): `, endTime - startTime);
        }
        this.bitmapLODs.set(lod, bitmap);
        return bitmap;
    };
    prepareImageUrl = (isPreview = false) => {
        if (this.src.startsWith("data:image")) {
            return this.src;
        }

        const url = new URL(this.src);
        const token = canv.getInitDataProp("boardAccessToken");
        if (token) {
            url.searchParams.set("boardAccessToken", token);
        }
        if (isPreview) {
            url.searchParams.set(IMAGE_PREVIEW_PARAM_KEY, "true");
        }

        return normalizeImageUrl(url.href);
    };
    getOrInitImage(lod) {
        if (!this.src) {
            return;
        }
        if (this.loading || this.imageLoading || this.bitmapLODs.has(lod)) {
            return this.getImageByLod(lod);
        }

        if (this.image) {
            // есть оригинал картинки, но нет соответствущего LOD - генерим и возвращаем фолбэк
            this.imageLoading = true;
            this.generateBitmap(this.image, lod).finally(() => {
                this.imageLoading = false;
            });
            return this.getImageByLod(lod);
        }

        const isPreview = lod === LOD_LOW;
        const isLocalImage = this.src.startsWith("data:image");

        // нет оригинала картинки и соответствующего LOD - грузим картинку, генерим и возвращаем фолбэк
        this.imageLoading = true;
        imageLoadLimiter(() => {
            const url = this.prepareImageUrl(isPreview);

            return createImage(url, true).then((image) => {
                this.afterLoad(image, this.forcedProps);
                return this.generateBitmap(image, lod).finally(() => {
                    if (!isPreview && !isLocalImage) {
                        this.image = image;
                    }
                    this.imageLoading = false;
                    canv.redraw();
                });
            });
        });

        return this.getImageByLod(lod);
    }

    clearPreviousImage(keepBase64 = false) {
        this.image = null;
        this.bitmapLODs.delete(LOD_MEDIUM);
        this.bitmapLODs.delete(LOD_HIGH);
        this.bitmapLODs.delete(LOD_LOW_HIGH);
        if (!keepBase64) {
            this.bitmapLODs.delete(LOD_LOW);
        }
        this.imageLoading = false;
    }

    initImage(src, forcedProps = undefined, keepBase64 = false) {
        this.src = src;
        // forcedProps храним, чтобы использовать в afterLoad и затем очистить
        this.forcedProps = forcedProps;
        this.clearPreviousImage(keepBase64);
    }

    setLoading(loading) {
        this.loading = loading;
    }

    afterLoad = (image, forcedProps) => {
        this.originalHeight = image.height;
        this.originalWidth = image.width;

        if (forcedProps != undefined) {
            this.width = forcedProps["miroWidth"];
            const realRatio = this.originalHeight / this.originalWidth;
            this.height = this.width * realRatio;

            this.startX = forcedProps["miroX"] - this.width / 2;
            this.startY = forcedProps["miroY"] - this.height / 2;
            this.updateDims();
            this.needFullState = true; // тут может быть gap загрузкой в loadState и выставлением this.needFullState.
        }
        this.forcedProps = undefined;

        canv.redraw();
    };

    ///////////////

    getWidgetOptions() {
        const widgetOptions = [];
        widgetOptions.push(new widgetOption("media", "", this.UID));
        widgetOptions.push(new widgetOption("clip", "", this.UID));

        return widgetOptions;
    }

    getHandlerForOption(name) {
        if (name == "media") {
            return this.changeImage;
        } else if (name == "clip") {
            return this.clipImage;
        }
    }

    getFixType(reason) {
        var fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;
            case "resize":
                fixType = "resize";
                break;
        }

        if (this.needFullState) {
            // this.needFullState = false; делать нельзя, так как getState дергается два раза для одного эвента
            // для этого instance image мы будем сохранять всегда full
            return "full";
        }

        return fixType;
    }

    getState(fixType) {
        var state = {};

        switch (fixType) {
            case "move":
                state["startX"] = this.startX;
                state["startY"] = this.startY;
                break;
            case "resize":
                state["startX"] = this.startX;
                state["startY"] = this.startY;
                state["width"] = this.width;
                state["height"] = this.height;
                break;
            case "full":
                state["UID"] = this.UID;
                state["parentUID"] = this.parentUID;
                state["entityType"] = "imageWidget";

                state["src"] = this.src;
                if (this.copyImageDataUrl != undefined) {
                    state["copyImageDataUrl"] = this.copyImageDataUrl;
                }

                state["startX"] = this.startX;
                state["startY"] = this.startY;
                state["width"] = this.width;
                state["height"] = this.height;

                state["clipDxShare"] = this.clipDxShare;
                state["clipDyShare"] = this.clipDyShare;
                state["clipWidthShare"] = this.clipWidthShare;
                state["clipHeightShare"] = this.clipHeightShare;

                // зануляем всегда, сработает в loadState при загрузке доски первым пользователем. Далее уже не будем смотреть на эти поля
                state["miroX"] = 0;
                state["miroY"] = 0;
                state["miroWidth"] = 0;
                break;
        }

        return state;
    }

    loadState(stateObj, fixType) {
        switch (fixType) {
            case "move":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                break;
            case "resize":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                break;
            case "full":
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);

                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);

                let forcedProps = undefined;
                if (
                    stateObj["miroX"] != undefined &&
                    stateObj["miroY"] != undefined &&
                    stateObj["miroWidth"] != undefined &&
                    stateObj["miroWidth"] != 0
                ) {
                    forcedProps = {
                        miroX: getNumberProperty("miroX", stateObj),
                        miroY: getNumberProperty("miroY", stateObj),
                        miroWidth: getNumberProperty("miroWidth", stateObj),
                    };
                }

                this.initImage(stateObj["src"], forcedProps);

                if (stateObj["copyImageDataUrl"] != undefined) {
                    this.pasteImageDataUrl = stateObj["copyImageDataUrl"];
                }

                this.clipDxShare = getNumberProperty("clipDxShare", stateObj, 0);
                this.clipDyShare = getNumberProperty("clipDyShare", stateObj, 0);
                this.clipWidthShare = getNumberProperty("clipWidthShare", stateObj, 1);
                this.clipHeightShare = getNumberProperty(
                    "clipHeightShare",
                    stateObj,
                    1,
                );
                break;
        }

        this.updateDims();
    }

    constructorName = "image";
    src = "";
    bitmapLODs = new Map();
    image = null;
    width = 0;
    height = 0;
    startX = 0;
    startY = 0;

    originalWidth = 0;
    originalHeight = 0;
    clipDxShare = 0;
    clipDyShare = 0;
    clipWidthShare = 1;
    clipHeightShare = 1;

    // этот флажок по дефолту false и не выгружается
    isClipping = false;

    copyImageDataUrl = undefined;
    pasteImageDataUrl = undefined;

    needFullState = false;
}
